import { Plugin } from 'prosemirror-state';

import { standard_schema } from './wikitext/index';
import EnterManager from './enter_manager';
import ProseMirror from './prosemirror';
import KeydownEvents from './keydown';
import filterParsers from './parser_filter';
import { ProseMirrorEditorHandle } from './editor_handle';

import * as CharCounter from '../char_counter';

/**
 * This function consumes an editorMapping object and a ProseMirror container
 * Element and creates a new ProseMirrorEditorHandle for the add interface.
 *
 * The new handle is placed at `comment-form-{step,guide}-<{step,guide}id>` in
 * the editorMapping object.
 */
function initEditor(editorMapping, editorEl) {
   const editorId = editorEl.id;

   if (editorMapping[editorId]) {
      // Already init'd don't make another
      return editorMapping[editorId];
   }

   const keymapping = {
      'Mod-Enter': () => {
         //if form submit it, else click button
         if (form) {
            const commentManager = editorEl.retrieve('commentManager');
            commentManager.submit();
         } else {
            const editDiv = editorEl.getElementsByClassName('commentEditDiv')[0];
            const submitBtnClass = editDiv.get('data-submit-button');

            const submitBtn = editDiv.getElementsByClassName(submitBtnClass)[0];
            submitBtn.click();
         }
      },
   };
   const clearErrors = () => {
      const formManager = editorEl.retrieve('FormManager:formManager');

      if (formManager) {
         formManager.clearIfError(editorEl);
      }
   };

   const pluginData = {
      keymapping,
      clearErrors,
   };

   const text = editorEl.dataset['text'];

   if (text) {
      pluginData['doc'] = JSON.parse(text);
   }

   const handle = createHandle(editorMapping, editorEl, pluginData);
   // Set up character counting on editors
   CharCounter.activateOnEditor(handle);

   return handle;
}

/**
 * Shared functionality between comment add and comment edit forms.
 *
 * @param editorMapping Object of comment form names to comment handles.
 * @param editorEl   Container form div
 * @param pluginData    An optional object containing a
 *                         keymappings object for ProseMirror
 *                         clearErrors function for the FormManager
 *                         doc object to start the editor with
 */
function createHandle(editorMapping, editorEl, pluginData) {
   const { keymapping, clearErrors, doc } = pluginData;
   const editorId = editorEl.id;

   const wikitags = JSON.parse(editorEl.dataset['wikitags']);
   const schema = filterParsers(standard_schema.schema, wikitags);

   const manager = new EnterManager();
   const keydownEvents = [manager.handleKeyDown.bind(manager)];
   const plugins = [];

   if (clearErrors) {
      keydownEvents.unshift(clearErrors);

      const blurPlugin = new Plugin({
         props: {
            onBlur: (view, event) => {
               window.formManager.clearIfError(editorEl);

               return true;
            },
         },
      });

      plugins.push(blurPlugin);
   }

   const keydownPlugin = new KeydownEvents(keydownEvents);

   plugins.push(keydownPlugin);
   const editor = ProseMirror(editorEl, schema, keymapping, plugins, doc);
   const handle = new ProseMirrorEditorHandle(editor, editorEl, manager);

   editor.dom.addClass('js-user-mention');
   editor.dom.addClass('js-comment-content');
   // This editorId allows another piece of code with just the editor's
   // container element to find the functions to make this editor do things by
   // looking them up in the `editorMapping` object.
   editor.dom.dataset['editorId'] = editorId;
   editorMapping[editorId] = handle;

   return handle;
}

const editorMapping = {};

// Only init the editors if they are specified to be eager loaded
// Otherwise, it's up dev to window.prosemirrorComments.getOrCreateEditor(el)
// At the right time for the page

// Note: slice turns nodelist into an array
const newCommentLocations = Array.prototype.slice.call(
   document.querySelectorAll('.add-comment-form .prosemirror.eagerload')
);
const editCommentLocations = Array.prototype.slice.call(
   document.querySelectorAll('.commentEditDiv .prosemirror.eagerload')
);

const allLocations = newCommentLocations.concat(editCommentLocations);

const handles = allLocations.map(initEditor.bind(null, editorMapping));

// Set up at-mention completion on editor contenteditables
const textCompleteable = handles.filter(h => h.el.classList.contains('js-user-mention-editor'));
initTextComplete(textCompleteable.map(h => h.editable));

window.editorMapping = editorMapping;
window.prosemirrorComments = {
   getOrCreateEditor: editorEl => {
      const editorId = editorEl.id;

      if (editorMapping[editorId]) {
         // Already init'd don't make another
         return editorMapping[editorId];
      }

      const handle = initEditor(editorMapping, editorEl);
      const needsMentionEditor = handle.el.classList.contains('js-user-mention-editor');
      if (needsMentionEditor) {
         initTextComplete(handle.editable);
      }

      return handle;
   },
};
